import { useState } from 'react';
import { useIntl } from 'react-intl';
import {
    Box,
    Button,
    ButtonGroup,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Menu,
    MenuItem,
    paletteColors,
    TextField,
    Theme,
    toast,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@evaneos/b2b-ui';
import ArrowDropDownIcon from '@evaneos/b2b-ui/icons/KeyboardArrowDown';
import PhoneIcon from '@evaneos/b2b-ui/icons/LocalPhoneOutlined';
import VideoCallIcon from '@evaneos/b2b-ui/icons/VideoCallOutlined';

import { usePhoneCall } from '../../../../shared/providers/PhoneCallContext';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';
import { DossierId } from '../../../../resources/dossier/types';
import { useGetDossierData } from '../../../../resources/dossier/queries';
import {
    isTwilioSDKSupported,
    phoneNumberFromString,
    validatePhoneNumber,
} from '../../../../shared/utilities/phone';
import { ExpandableContainer } from '../../../../design-system/ExpandableContainer/ExpandableContainer';
import { useSidePanel } from '../../SidePanel/SidePanelContext';

const MEET_URL = 'https://meet.evaneos.com';

/* eslint-disable-next-line complexity, max-lines-per-function -- multiple phone validation states and error rendering */
export const CallButtonGroup = ({ dossierId }: { dossierId: DossierId }) => {
    const { openModal, isModalOpen } = usePhoneCall();
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);
    const { isOpen: isSidePanelOpen } = useSidePanel();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    const isLargeScreenSize = useMediaQuery((theme: Theme) => theme.breakpoints.up('xxl'));
    const [toggleMenu, setToggleMenu] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
    const [openVideoCallModal, setOpenVideoCallModal] = useState<boolean>(false);

    const { dossier } = useGetDossierData(dossierId) || {};
    const destinationName = dossier?.project?.destinationLabel || 'Room';
    const videoLink = generateLink(MEET_URL, destinationName, dossierId);
    const requesterPhone = dossier?.project?.requesterPhone;

    const isRequesterPhoneNumberExist = !!requesterPhone && requesterPhone.trim() !== '';
    const phoneNumber = phoneNumberFromString(requesterPhone || '');
    const isRequesterPhoneNumberValid = validatePhoneNumber(phoneNumber);
    const isWebRTCSupported = isTwilioSDKSupported();
    const isMinified = isMobile || (isSidePanelOpen && !isLargeScreenSize);

    const hasError =
        !isRequesterPhoneNumberExist || !isRequesterPhoneNumberValid || !isWebRTCSupported;
    const isPhoneDisabled = hasError || isModalOpen;

    const handleOpenPhoningModal = () => {
        setToggleMenu(false);
        if (isPhoneDisabled) {
            return;
        }
        openModal(dossierId);
        trackEvent((ampli, defaultProperties) => {
            ampli.travelerCallClicked({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
    };

    const handleOpenVideoCallModal = () => {
        setToggleMenu(false);
        setOpenVideoCallModal(true);
        trackEvent((ampli, defaultProperties) => {
            ampli.videoLinkGenerated({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
    };

    const onVideoLinkCopyClick = () => {
        toast.success(formatMessage({ id: 'discussion.meeting_room.copy_success' }));
        trackEvent((ampli, defaultProperties) => {
            ampli.meetingRoomCopied({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
    };

    const renderPhoneCallErrorTooltip = () => {
        if (!isRequesterPhoneNumberExist) {
            return formatMessage({ id: 'phoning.missing-traveler-number' });
        }
        if (!isRequesterPhoneNumberValid) {
            return formatMessage({ id: 'phoning.invalid-traveler-number' });
        }
        if (!isRequesterPhoneNumberValid) {
            return formatMessage({ id: 'phoning.webrtc-unsupported' });
        }
        return '';
    };

    return (
        <>
            <ButtonGroup
                variant="contained"
                ref={setAnchorEl}
                aria-label="PhoneCall and more menu"
                disableElevation
                disableRipple
                sx={{
                    '& .MuiButtonGroup-grouped': {
                        minWidth: 'auto',
                        borderRight: 'none',
                    },
                }}
            >
                <Tooltip title={renderPhoneCallErrorTooltip()}>
                    <Box display="flex">
                        <Button
                            onClick={handleOpenPhoningModal}
                            startIcon={isMinified ? undefined : <PhoneIcon />}
                            disabled={isPhoneDisabled}
                            data-testid="phone-call-button"
                            sx={{
                                position: 'relative',
                                p: isMinified ? 1 : undefined,
                                minWidth: isMinified ? 0 : undefined,
                                width: isMinified ? '40px' : undefined,
                                height: isMinified ? '40px' : undefined,
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    right: 0,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    height: '60%',
                                    width: '1px',
                                    backgroundColor: paletteColors.neutral[0],
                                },
                            }}
                        >
                            {isMinified ? <PhoneIcon /> : formatMessage({ id: 'phoning.call' })}
                        </Button>
                    </Box>
                </Tooltip>
                <Button
                    aria-controls={toggleMenu ? 'split-button-menu' : undefined}
                    aria-expanded={toggleMenu ? 'true' : undefined}
                    aria-label="more menu"
                    aria-haspopup="menu"
                    onClick={() => setToggleMenu((prevOpen) => !prevOpen)}
                    sx={{
                        paddingX: 1,
                    }}
                >
                    <ArrowDropDownIcon />
                </Button>
            </ButtonGroup>
            <Menu
                id="split-button-menu"
                open={toggleMenu}
                anchorEl={anchorEl}
                onClose={() => setToggleMenu(false)}
                slotProps={{
                    paper: {
                        sx: {
                            '& .MuiList-root': {
                                paddingTop: 0,
                                paddingBottom: 0,
                                '& .MuiMenuItem-root': {
                                    gap: 0.5,
                                    paddingY: 2,
                                    fontSize: '1.4rem',
                                    lineHeight: '2rem',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    '&:last-child': {
                                        borderBottom: 'none',
                                    },
                                },
                            },
                        },
                    },
                }}
            >
                <MenuItem onClick={handleOpenPhoningModal} disabled={isPhoneDisabled}>
                    <PhoneIcon fontSize="small" />
                    {formatMessage({ id: 'phoning.phone-to-phone' })}
                </MenuItem>
                <MenuItem onClick={handleOpenVideoCallModal}>
                    <VideoCallIcon fontSize="small" />
                    {formatMessage({
                        id: 'experiment.better_business.video_call',
                    })}
                </MenuItem>
            </Menu>

            <Dialog
                open={openVideoCallModal}
                onClose={() => setOpenVideoCallModal(false)}
                maxWidth="md"
                fullWidth
                slotProps={{
                    paper: {
                        sx: { padding: 2 },
                    },
                }}
            >
                <DialogTitle variant="h2">
                    {formatMessage({
                        id: 'discussion.meeting_room.modal.title',
                    })}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        {formatMessage({
                            id: 'discussion.meeting_room.modal.description_link_created',
                        })}
                    </Typography>
                    <Typography variant="body2">
                        {formatMessage({
                            id: 'discussion.meeting_room.modal.description_link_share',
                        })}
                    </Typography>
                    <ExpandableContainer
                        previewTitle={formatMessage({
                            id: 'discussion.meeting_room.modal.preview_title',
                        })}
                        content={
                            <Box
                                sx={{
                                    position: 'relative',
                                    paddingBottom: '53.25%',
                                }}
                            >
                                <Box
                                    component="iframe"
                                    sx={{
                                        position: 'absolute',
                                        width: '100%',
                                        height: '90%',
                                        left: 0,
                                        top: 0,
                                        border: 0,
                                    }}
                                    allowFullScreen
                                    src="https://playplay.com/app/embed-video/d0c95261-e1dc-41aa-b938-6914cb36e895"
                                />
                            </Box>
                        }
                    />
                </DialogContent>
                <DialogActions
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                    data-testid="meeting-room-link-modal"
                >
                    <TextField
                        variant="outlined"
                        size="small"
                        aria-label="Meeting room link"
                        value={videoLink}
                        disabled
                        sx={{
                            flexGrow: 1,
                            backgroundColor: paletteColors.neutral[200],
                            '& input.Mui-disabled': {
                                color: paletteColors.neutral[900],
                                WebkitTextFillColor: paletteColors.neutral[900],
                            },
                        }}
                    />
                    <Button
                        onClick={() => {
                            handleCopyButtonClick(videoLink, onVideoLinkCopyClick);
                            setOpenVideoCallModal(false);
                        }}
                    >
                        {formatMessage({
                            id: 'discussion.meeting_room.modal.copy',
                        })}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

function generateLink(meetUrl: string, destinationName: string, dossierId: string): string {
    const roomId = encodeURIComponent(`${destinationName}`) + `@${dossierId}`;
    return `${meetUrl}/room/${roomId}`;
}

function handleCopyButtonClick(link: string, callback: () => void): void {
    void navigator.clipboard.writeText(link).then(() => callback());
}
